import { isMoment, Moment } from "moment";
import { DateAdapter } from "@angular/material";
import * as moment from "moment";
import { dateNames } from "../date-formats/moment-date-formats";

export class MomentDateAdapter extends DateAdapter<Moment> {
    constructor() {
        super();
    }
    private localeData = moment.localeData();

    isValid(date: any): boolean {
        if (!date) {
            return true;
        }
        return date instanceof moment || moment(date).isValid();
    }

    isDateInstance(date: any): boolean {
        if (!date) {
            return true; //always allow when date is undefined
        }
        return date instanceof moment || moment(date).isValid();
    }

    invalid(): Moment {
        return moment.invalid();
      }

    fromIso8601(date: string): Moment {
        return moment(date);
    }

    toIso8601(date: any): string {
        return moment(date).toISOString();
    }

    getYear(date: Moment): number {
        return moment(date).year();
    }

    getMonth(date: Moment): number {
        return moment(date).month();
    }

    getDate(date: Moment): number {
        return moment(date).date();
    }

    getDayOfWeek(date: Moment): number {
        return moment(date).day();
    }

    getMonthNames(style: "long" | "short" | "narrow"): string[] {
        switch (style) {
            case "long":
                return this.localeData.months();
            case "short":
                return this.localeData.monthsShort();
            case "narrow":
                return this.localeData.monthsShort().map(month => month[0]);
        }
    }

    getDateNames(): string[] {
        return dateNames;
    }

    getDayOfWeekNames(style: "long" | "short" | "narrow"): string[] {
        switch (style) {
            case "long":
                return this.localeData.weekdays();
            case "short":
                return this.localeData.weekdaysShort();
            case "narrow":
                // Moment does not accept format even though @types/moment suggests it does
                return this.localeData.weekdaysShort();
        }
    }

    getYearName(date: Moment): string {
        return String(moment(date).year());
    }

    getFirstDayOfWeek(): number {
        return this.localeData.firstDayOfWeek();
    }

    getNumDaysInMonth(date: Moment): number {
        return moment(date).daysInMonth();
    }

    clone(date: Moment): Moment {
        return moment(date).clone();
    }

    createDate(year: number, month: number, date: number): Moment {
        return moment([year, month, date]);
    }

    today(): Moment {
        return moment();
    }

    parse(value: any, parseFormat: any): Moment {
        let m = moment(value, parseFormat, true);
        if (!m.isValid()) {
            // try again, forgiving. will get warning if not ISO8601 or RFC2822
            m = moment(value, [
                "MM/DD/YY",
                "MM-DD-YY",
                "MM/DD/YYYY",
                "MM-DD-YYYY"
            ]);
        }
        if (m.isValid()) {
            // if user omits year, it defaults to 2001, so check for that issue.
            if (m.year() === 2001 && value.indexOf("2001") === -1) {
                // if 2001 not actually in the value string, change to current year
                const currentYear = new Date().getFullYear();
                m.set("year", currentYear);
                // if date is in the future, set previous year
                if (m.isAfter(moment())) {
                    m.set("year", currentYear - 1);
                }
            }
            return m;
        } else {
            return null;
        }
    }

    format(date: Moment, displayFormat: any): string {
        if (date) {
            date = moment(date);
            return date.format(displayFormat);
        } else {
            return "";
        }
    }

    addCalendarYears(date: Moment, years: number): Moment {
        return moment(date)
            .clone()
            .add(years, "y");
    }

    addCalendarMonths(date: Moment, months: number): Moment {
        return moment(date)
            .clone()
            .add(months, "M");
    }

    addCalendarDays(date: Moment, days: number): Moment {
        return moment(date)
            .clone()
            .add(days, "d");
    }

    getISODateString(date: Moment): string {
        return moment(date).toISOString();
    }

    setLocale(locale: any): void {
        // console.info('setLocale', locale);
        this.localeData = moment.localeData(locale);
    }

    compareDate(first: Moment, second: Moment): number {
        if (first instanceof moment) {
            return first.diff(second, "seconds", true);
        }
        return moment(first).diff(second, "seconds", true);
    }

    sameDate(first: any | Moment, second: any | Moment): boolean {
        if (first == null) {
            // same if both null
            return second == null;
        } else if (isMoment(first)) {
            return first.isSame(second);
        } else {
            const isSame = super.sameDate(first, second);
            // console.warn('first not a Moment. fallback to super.sameDate()', first, second, isSame);
            return isSame;
        }
    }

    clampDate(date: Moment, min?: any | Moment, max?: any | Moment): Moment {
        if (min && moment(date).isBefore(min)) {
            return min;
        } else if (max && moment(date).isAfter(max)) {
            return max;
        } else {
            return moment(date);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/date-adapter/moment-date-adapter.class.ts